import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, Sparkles, Play, Pause, RotateCw, Download, ChevronRight, Loader2, AlertCircle, Check, Wand2, Cpu } from "lucide-react";
import * as Slider from "@radix-ui/react-slider";
import { Player } from "../animation/Player";
import { exportToBlob } from "../animation/export";
import { api, type AnimationDto } from "../lib/api";
import { Spinner } from "../components/Spinner";
import { compileTimeline } from "@app/shared/templates";
import { TEMPLATE_INFO } from "@app/shared/templates";
import { AnimationIR, Easing, Effect, Mood, TemplateId, ModelId, MODEL_INFO, type Timeline } from "@app/shared/ir";
import { analyzeSvg, tagSvgPaths } from "@app/shared/analyze";
import { AI_BRANDS } from "../animation/ai-icons";
import { cn } from "../lib/cn";

interface Props { mode: "new" | "existing" }

export function Editor({ mode }: Props) {
  const params = useParams();
  const nav = useNavigate();
  const [phase, setPhase] = useState<"upload" | "prompt" | "loading" | "edit">(mode === "new" ? "upload" : "loading");

  // Editor state
  const [animation, setAnimation] = useState<AnimationDto | null>(null);
  const [svgText, setSvgText] = useState<string>("");
  const [prompt, setPrompt] = useState("");
  const [assetId, setAssetId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [model, setModel] = useState<typeof ModelId.options[number]>("llama-3.3-70b");

  // Local IR overrides for live preview before commit.
  const [localIR, setLocalIR] = useState<any | null>(null);

  // Load existing animation
  useEffect(() => {
    if (mode !== "existing" || !params.id) return;
    (async () => {
      try {
        const r = await api.getAnimation(params.id!);
        setAnimation(r.animation);
        setLocalIR(r.animation.ir);
        if (r.asset?.id) {
          const text = await fetch(api.assetUrl(r.asset.id), { credentials: "include" }).then((x) => x.text());
          setSvgText(tagSvgPaths(text));
        }
        if (r.animation.status === "analyzing" || r.animation.status === "planning" || r.animation.status === "queued") {
          setPhase("loading");
          pollUntilReady(r.animation.id);
        } else if (r.animation.status === "ready" && r.animation.timeline) {
          setPhase("edit");
        } else if (r.animation.status === "failed") {
          setErr(r.animation.errorMsg ?? "Plan failed.");
          setPhase("prompt"); // let user retry
        }
      } catch (e: any) {
        setErr(e?.message ?? "Failed to load animation.");
      }
    })();
  }, [mode, params.id]);

  async function pollUntilReady(id: string) {
    setPhase("loading");
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const r = await api.getAnimation(id);
        setAnimation(r.animation);
        if (r.animation.status === "ready") {
          setLocalIR(r.animation.ir);
          if (r.asset?.id && !svgText) {
            const text = await fetch(api.assetUrl(r.asset.id), { credentials: "include" }).then((x) => x.text());
            setSvgText(tagSvgPaths(text));
          }
          setPhase("edit");
          return;
        }
        if (r.animation.status === "failed") {
          setErr(r.animation.errorMsg ?? "Plan failed.");
          setPhase("prompt");
          return;
        }
      } catch { /* keep polling */ }
    }
    setErr("Timed out waiting for plan.");
  }

  // Live preview uses a freshly compiled timeline against current localIR.
  const previewTimeline = useMemo<Timeline | null>(() => {
    if (!svgText || !localIR) return null;
    try {
      const ir = AnimationIR.parse(localIR);
      const analysis = analyzeSvg(svgText);
      return compileTimeline(ir, analysis);
    } catch {
      return animation?.timeline ?? null;
    }
  }, [svgText, localIR, animation?.timeline]);

  async function onFile(file: File) {
    setErr(null); setUploading(true);
    try {
      let svgFile = file;
      if (file.type !== "image/svg+xml") {
        // Future: route through Potrace WASM. For now, accept the original and
        // let the server-side analyzer fall back to a bounding-box-only plan.
        // We rasterise → vectorise client-side in a follow-up.
        const dataUrl = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result as string); r.onerror = rej; r.readAsDataURL(file); });
        // Embed the raster as an SVG <image> so we still get an SVG container.
        const wrapped = `<svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg"><image href="${dataUrl}" x="0" y="0" width="1000" height="1000" preserveAspectRatio="xMidYMid meet"/></svg>`;
        svgFile = new File([wrapped], file.name.replace(/\.[^.]+$/, "") + ".svg", { type: "image/svg+xml" });
      }
      const r = await api.uploadLogo(svgFile);
      setAssetId(r.asset.id);
      // Pull the canonical bytes back so the preview matches what the API sees.
      const text = await fetch(api.assetUrl(r.asset.id), { credentials: "include" }).then((x) => x.text());
      setSvgText(tagSvgPaths(text));
      setPhase("prompt");
    } catch (e: any) {
      setErr(e?.message ?? "Upload failed.");
    } finally { setUploading(false); }
  }

  async function startPlan() {
    if (!assetId || !prompt.trim()) return;
    setPlanning(true); setErr(null);
    try {
      const r = await api.createAnimation(assetId, prompt.trim(), { model });
      nav(`/editor/${r.animation.id}`, { replace: true });
    } catch (e: any) {
      setErr(e?.message ?? "Failed to start plan.");
      setPlanning(false);
    }
  }

  async function persistIR() {
    if (!animation || !localIR) return;
    const r = await api.patchIR(animation.id, localIR);
    setAnimation(r.animation);
  }

  async function regenerate() {
    if (!animation) return;
    setPhase("loading"); setErr(null);
    try {
      await api.regenerate(animation.id, { prompt: prompt || undefined, model });
      pollUntilReady(animation.id);
    } catch (e: any) {
      setErr(e?.message ?? "Regenerate failed.");
      setPhase("edit");
    }
  }

  // ---------- render branches ----------
  if (phase === "upload") return <UploadStep onFile={onFile} uploading={uploading} err={err} />;
  if (phase === "prompt") return (
    <PromptStep
      svgText={svgText}
      prompt={prompt}
      onChange={setPrompt}
      onSubmit={startPlan}
      planning={planning}
      err={err}
      onBack={() => setPhase("upload")}
      model={model}
      onModelChange={setModel}
    />
  );
  if (phase === "loading") return <LoadingStep status={animation?.status ?? "queued"} prompt={animation?.prompt ?? prompt} />;

  return (
    <EditStage
      svgText={svgText}
      animation={animation!}
      localIR={localIR}
      timeline={previewTimeline}
      onIRChange={setLocalIR}
      onPersist={persistIR}
      onRegenerate={regenerate}
      onUpdatePrompt={setPrompt}
      prompt={prompt || animation?.prompt || ""}
      err={err}
      model={model}
      onModelChange={setModel}
    />
  );
}

/* ============================================================================
 * Upload step
 * ========================================================================== */
function UploadStep({ onFile, uploading, err }: { onFile: (f: File) => void; uploading: boolean; err: string | null }) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [drag, setDrag] = useState(false);
  return (
    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
      <StepHeader step={1} title="Upload your logo" subtitle="SVG is preferred. PNG and JPG work — we'll embed them." />
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault(); setDrag(false);
          const f = e.dataTransfer.files?.[0]; if (f) onFile(f);
        }}
        className={cn(
          "card grid place-items-center px-6 py-20 text-center transition-colors",
          drag ? "border-white/30 bg-white/[0.05]" : ""
        )}
      >
        <Upload className="mb-3 h-8 w-8 text-white/40" />
        <p className="text-white">Drop your logo here</p>
        <p className="mt-1 text-sm text-white/40">SVG · PNG · JPG (max 5 MB)</p>
        <button onClick={() => fileRef.current?.click()} className="btn-primary mt-5" disabled={uploading}>
          {uploading ? <Spinner /> : <Upload className="h-4 w-4" />}
          {uploading ? "Uploading…" : "Choose file"}
        </button>
        <input ref={fileRef} type="file" accept="image/svg+xml,image/png,image/jpeg" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
      </div>
      {err && <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{err}</div>}
    </div>
  );
}

/* ============================================================================
 * Prompt step
 * ========================================================================== */
const PROMPT_SUGGESTIONS = [
  "Cinematic luxury reveal, slow zoom, subtle shadow.",
  "Bouncy and playful, for a kids brand.",
  "Neon cyberpunk glitch, RGB split, scanlines.",
  "Hand-drawn stroke reveal, then a soft fill.",
  "Liquid wave sweep, calm and confident.",
];
function PromptStep({ svgText, prompt, onChange, onSubmit, planning, err, onBack, model, onModelChange }: {
  svgText: string;
  prompt: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  planning: boolean;
  err: string | null;
  onBack: () => void;
  model: typeof ModelId.options[number];
  onModelChange: (m: typeof ModelId.options[number]) => void;
}) {
  return (
    <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[1.1fr_1fr]">
      <div>
        <StepHeader step={2} title="Describe how it should move" subtitle="Mood, pace, vibe. Plain English. Our AI picks a template and tunes the parameters." />
        <textarea
          className="field min-h-[160px] resize-none text-base"
          placeholder="e.g. premium luxury reveal, slow zoom, subtle shadow"
          value={prompt}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
        />
        <div className="mt-3 flex flex-wrap gap-1.5">
          {PROMPT_SUGGESTIONS.map((s) => (
            <button key={s} type="button" onClick={() => onChange(s)} className="rounded-full border border-white/10 bg-white/[0.02] px-2.5 py-1 text-[11px] text-white/55 hover:bg-white/[0.05] hover:text-white">
              {s}
            </button>
          ))}
        </div>

        <div className="mt-6 card p-4">
          <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/60">
            <Cpu className="h-3.5 w-3.5" /> Model
          </div>
          <ModelPicker value={model} onChange={onModelChange} />
        </div>

        {err && <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{err}</div>}
        <div className="mt-6 flex items-center gap-2">
          <button className="btn-ghost" onClick={onBack}>Back</button>
          <button className="btn-primary" disabled={!prompt.trim() || planning} onClick={onSubmit}>
            {planning ? <Spinner /> : <Sparkles className="h-4 w-4" />}
            {planning ? "Planning…" : "Generate animation"}
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="card aspect-square w-full overflow-hidden grid-bg">
        <div className="grid h-full place-items-center p-6">
          {svgText
            ? <div className="h-full w-full [&_svg]:max-h-full [&_svg]:max-w-full" dangerouslySetInnerHTML={{ __html: svgText }} />
            : <span className="text-sm text-white/30">No logo</span>}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * Loading step
 * ========================================================================== */
function LoadingStep({ status, prompt }: { status: string; prompt: string }) {
  const phases = ["queued", "analyzing", "planning", "ready"];
  const idx = Math.max(0, phases.indexOf(status));
  return (
    <div className="mx-auto grid min-h-[70vh] max-w-3xl place-items-center px-4">
      <div className="w-full">
        <h2 className="text-2xl font-semibold tracking-tight text-white">Cooking up your animation</h2>
        <p className="mt-1 text-sm text-white/50">“{prompt}”</p>
        <div className="mt-8 space-y-3">
          {["Analyzing the logo geometry", "Asking Llama 3.3 to pick a template", "Compiling the timeline", "Ready to edit"].map((label, i) => (
            <div key={label} className="flex items-center gap-3">
              {i < idx ? <Check className="h-4 w-4 text-emerald-300" /> : i === idx ? <Loader2 className="h-4 w-4 spin text-white/80" /> : <span className="h-4 w-4 rounded-full border border-white/10" />}
              <span className={cn("text-sm", i <= idx ? "text-white" : "text-white/40")}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * Edit stage — the actual editor
 * ========================================================================== */
function EditStage({ svgText, animation, localIR, timeline, onIRChange, onPersist, onRegenerate, onUpdatePrompt, prompt, err, model, onModelChange }: {
  svgText: string;
  animation: AnimationDto;
  localIR: any | null;
  timeline: Timeline | null;
  onIRChange: (ir: any) => void;
  onPersist: () => void;
  onRegenerate: () => void;
  onUpdatePrompt: (v: string) => void;
  prompt: string;
  err: string | null;
  model: typeof ModelId.options[number];
  onModelChange: (m: typeof ModelId.options[number]) => void;
}) {
  const [paused, setPaused] = useState(false);
  const [loopKey, setLoopKey] = useState(0); // force-restart on change
  const [exporting, setExporting] = useState<{ progress: number } | null>(null);
  const [exportErr, setExportErr] = useState<string | null>(null);
  const [exported, setExported] = useState<{ url: string; filename: string } | null>(null);

  // Restart preview whenever the timeline shape changes. We also key on
  // templateId so switching templates doesn't surprise the user with stale
  // path-level inline styles left over from the previous template.
  const currentTemplate = (localIR ?? animation.ir)?.templateId;
  useEffect(() => { setLoopKey((k) => k + 1); }, [timeline?.durationMs, timeline?.tracks.length, currentTemplate]);

  const ir = localIR ?? animation.ir;
  if (!ir || !timeline) return <div className="grid min-h-[60vh] place-items-center text-white/50"><Spinner /></div>;

  const setIRField = (k: string, v: any) => onIRChange({ ...ir, [k]: v });

  async function doExport() {
    if (!svgText || !timeline) return;
    setExportErr(null); setExported(null); setExporting({ progress: 0 });
    try {
      const { blob } = await exportToBlob({
        svg: svgText,
        timeline,
        width: animation.width,
        height: animation.height,
        fps: animation.fps,
        background: ir.colorOverrides?.transparent ? "transparent" : ir.colorOverrides?.background ?? "#0a0a0c",
        onProgress: (p) => setExporting({ progress: p }),
      });
      const filename = `motion-${animation.id}.webm`;
      // Upload to server so it shows up on the dashboard / can be re-downloaded.
      try { await api.uploadRender(animation.id, blob, "render_webm", filename); } catch { /* allow local download anyway */ }
      const url = URL.createObjectURL(blob);
      setExported({ url, filename });
    } catch (e: any) {
      setExportErr(e?.message ?? "Export failed.");
    } finally {
      setExporting(null);
    }
  }

  return (
    <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[1fr_380px]">
      {/* Preview */}
      <div className="card relative aspect-[4/3] overflow-hidden grid-bg">
        <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-white/[0.06] px-2 py-0.5 font-mono text-[10px] text-white/70">{ir.templateId}</span>
            <span className="font-mono text-[10px] text-white/30">{Math.round(timeline.durationMs)} ms · {timeline.fps} fps</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => { setPaused((p) => !p); setLoopKey((k) => k + 1); }} className="btn-quiet">
              {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </button>
            <button onClick={() => setLoopKey((k) => k + 1)} className="btn-quiet"><RotateCw className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="grid h-full place-items-center">
          <Player key={loopKey} svg={svgText} timeline={timeline} loop autoplay paused={paused} smoothSwap width={animation.width} height={animation.height} />
        </div>
      </div>

      {/* Sidebar */}
      <div className="space-y-4">
        <Section title="Prompt" right={<button onClick={onRegenerate} className="btn-quiet"><Wand2 className="h-3.5 w-3.5" />Regenerate</button>}>
          <textarea className="field min-h-[80px] resize-none" value={prompt} onChange={(e) => onUpdatePrompt(e.target.value)} />
        </Section>

        <Section title="Model">
          <ModelPicker value={model} onChange={onModelChange} />
        </Section>

        <Section title="Template">
          <div className="grid grid-cols-2 gap-2">
            {TemplateId.options.map((id) => (
              <button
                key={id}
                onClick={() => setIRField("templateId", id)}
                className={cn(
                  "rounded-lg border px-2.5 py-2 text-left text-xs transition-colors",
                  ir.templateId === id ? "border-white/30 bg-white/[0.06] text-white" : "border-white/10 bg-white/[0.02] text-white/55 hover:bg-white/[0.04]"
                )}
              >
                <div className="font-medium">{TEMPLATE_INFO[id]?.title}</div>
                <div className="font-mono text-[10px] text-white/30">{id}</div>
              </button>
            ))}
          </div>
        </Section>

        <Section title="Timing">
          <NumberSlider label="Duration" unit="ms" min={800} max={6000} step={50} value={ir.durationMs} onChange={(v) => setIRField("durationMs", v)} />
          <NumberSlider label="Speed" min={0.4} max={2.5} step={0.1} value={ir.speed} onChange={(v) => setIRField("speed", v)} />
          <Select label="Easing" value={ir.easing} options={Easing.options} onChange={(v) => setIRField("easing", v)} />
        </Section>

        <Section title="Mood">
          <Select label="Mood" value={ir.mood} options={Mood.options} onChange={(v) => setIRField("mood", v)} />
        </Section>

        <Section title="Effects">
          <EffectsEditor effects={ir.effects ?? []} onChange={(eff) => setIRField("effects", eff)} />
        </Section>

        <div className="flex items-center gap-2">
          <button onClick={onPersist} className="btn-ghost flex-1">Save changes</button>
          <button onClick={doExport} disabled={!!exporting} className="btn-primary flex-1">
            {exporting ? <Spinner /> : <Download className="h-4 w-4" />}
            {exporting ? `Exporting ${Math.round(exporting.progress * 100)}%` : "Export WebM"}
          </button>
        </div>

        {exported && (
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.06] p-3 text-sm">
            <div className="flex items-center gap-2 text-emerald-300"><Check className="h-4 w-4" /> Export ready</div>
            <a href={exported.url} download={exported.filename} className="mt-2 inline-block text-xs text-white underline-offset-2 hover:underline">
              Download {exported.filename}
            </a>
          </div>
        )}
        {exportErr && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            <AlertCircle className="mr-1.5 inline h-3.5 w-3.5" /> {exportErr}
          </div>
        )}
        {err && <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{err}</div>}
      </div>
    </div>
  );
}

function StepHeader({ step, title, subtitle }: { step: number; title: string; subtitle: string }) {
  return (
    <div className="mb-6">
      <div className="font-mono text-xs text-white/40">Step 0{step}</div>
      <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight text-white">{title}</h2>
      <p className="text-sm text-white/55">{subtitle}</p>
    </div>
  );
}

function Section({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-white/60">{title}</h3>
        {right}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function NumberSlider({ label, value, onChange, min, max, step, unit }: { label: string; value: number; onChange: (v: number) => void; min: number; max: number; step: number; unit?: string }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between text-xs">
        <span className="text-white/55">{label}</span>
        <span className="font-mono text-white/80">{value}{unit ?? ""}</span>
      </div>
      <Slider.Root value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0]!)} className="relative flex h-5 w-full touch-none select-none items-center">
        <Slider.Track className="relative h-[3px] grow rounded-full bg-white/10">
          <Slider.Range className="absolute h-full rounded-full bg-white/80" />
        </Slider.Track>
        <Slider.Thumb className="block h-3.5 w-3.5 rounded-full bg-white shadow ring-2 ring-black/40 focus:outline-none" />
      </Slider.Root>
    </div>
  );
}

function Select({ label, value, options, onChange }: { label: string; value: string; options: readonly string[]; onChange: (v: string) => void }) {
  return (
    <div>
      <div className="mb-1.5 text-xs text-white/55">{label}</div>
      <select className="field appearance-none" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function ModelPicker({ value, onChange }: {
  value: typeof ModelId.options[number];
  onChange: (m: typeof ModelId.options[number]) => void;
}) {
  // Map each model's iconId to a lazy lookup against AI_BRANDS so we keep
  // the brand SVG colours rather than monochrome.
  const iconFor = (iconId: string) => AI_BRANDS.find((b) => b.id === iconId)?.svg;
  return (
    <div className="grid grid-cols-1 gap-1.5">
      {ModelId.options.map((m) => {
        const info = MODEL_INFO[m];
        const active = value === m;
        const svg = iconFor(info.iconId);
        return (
          <button
            key={m}
            onClick={() => onChange(m)}
            className={cn(
              "flex items-center gap-3 rounded-lg border px-3 py-2 text-left text-xs transition-colors",
              active
                ? "border-white/30 bg-white/[0.06] text-white"
                : "border-white/10 bg-white/[0.02] text-white/60 hover:bg-white/[0.04] hover:text-white"
            )}
          >
            <span
              className="grid h-7 w-7 flex-none place-items-center rounded-md bg-white/[0.04] [&_svg]:h-4 [&_svg]:w-4"
              dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{info.title}</span>
              <span className="block truncate font-mono text-[10px] text-white/40">{info.provider}</span>
            </span>
            {active && <Check className="h-3.5 w-3.5 text-white/80" />}
          </button>
        );
      })}
    </div>
  );
}

function EffectsEditor({ effects, onChange }: { effects: { kind: string; intensity: number }[]; onChange: (v: { kind: string; intensity: number }[]) => void }) {
  const used = new Set(effects.map((e) => e.kind));
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {Effect.options.map((k) => (
          <button
            key={k}
            onClick={() => onChange(used.has(k) ? effects.filter((e) => e.kind !== k) : [...effects, { kind: k, intensity: 0.5 }].slice(0, 4))}
            className={cn(
              "rounded-md border px-2 py-1 text-[11px] transition-colors",
              used.has(k) ? "border-white/30 bg-white/[0.08] text-white" : "border-white/10 bg-white/[0.02] text-white/50 hover:bg-white/[0.05]"
            )}
          >{k}</button>
        ))}
      </div>
      {effects.map((e, i) => (
        <div key={e.kind} className="mt-3">
          <NumberSlider
            label={e.kind + " intensity"}
            value={e.intensity}
            onChange={(v) => onChange(effects.map((x, j) => j === i ? { ...x, intensity: v } : x))}
            min={0} max={1} step={0.05}
          />
        </div>
      ))}
    </div>
  );
}
